import React, { useEffect } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { Button } from "../../../shadcn/components/ui/button";
import { Input } from "../../../shadcn/components/ui/input";
import { LoadingSpinner } from "../../../icons/LoadingSpinner";
import { cn } from "../../../shadcn/lib/utils";

/** Empty defaults — field names align with asset-transfer-assets */
const initialAssetTransferFormValues = {
  borrower_name: "",
  borrower_entity_type: "",
  loan_account_number: "",
  sanction_amount: "",
  outstanding_principal: "",
  total_outstanding: "",
  dpd_days: "",
  npa_date: "",
  security_type: "",
  secured_asset_description: "",
  secured_asset_location: "",
  valuation_amount: "",
  valuation_date: "",
  legal_status: "",
  litigation_status: "",
  number_of_accounts: "",
  aggregate_outstanding: "",
  geographical_spread: "",
  portfolio_segment: "",
  data_cutoff_date: "",
  /** Set by system — record is created on behalf of banker */
  created_by: "banker",
  status: "",
};

type AssetTransferFormKey = keyof typeof initialAssetTransferFormValues;

/** Same height/padding as EventFormV2; value + label text one step smaller */
const floatingInputClass =
  "bg-white peer block h-[50px] w-full min-w-0 appearance-none rounded-none border pt-3 pb-0 pl-4 font-openSans font-normal text-sm leading-4 text-brand-textLightGrey outline-none focus:border-brand-orange focus:pb-0 focus:ring-0 disabled:cursor-not-allowed disabled:border-none disabled:bg-[#e5e7eb]";

const floatingLabelClass =
  "pointer-events-none absolute left-4 top-1/4 -translate-y-1/2 font-openSans text-[11px] font-normal leading-[13px] text-gray-400 transition-all duration-300 peer-focus:text-brand-orange";

const floatingTextareaClass =
  "peer block w-full min-w-0 appearance-none rounded-none border border-[#E5E7EB] bg-white pt-6 pb-0 pl-4 font-openSans font-normal text-web-body-sm leading-4 text-brand-textLightGrey outline-none focus:border-brand-orange focus:pb-0 focus:ring-0 disabled:border-none disabled:bg-[#e5e7eb]";

const floatingTextareaLabelClass =
  "pointer-events-none absolute left-4 top-[6px] font-openSans text-[11px] font-normal leading-[13px] text-gray-400 transition-all duration-300 peer-focus:text-brand-orange";

const sectionClass =
  "mt-6 box-border w-full min-w-0 max-w-full rounded-none bg-brand-bgLightGrey py-7 px-4 sm:px-6 lg:pl-[34px] lg:pr-[71px]";
const sectionTitleClass =
  "font-openSans font-semibold text-web-body-lg leading-6 text-brand-bgBlack";
const gridClass =
  "mt-5 flex min-w-0 max-w-full flex-col gap-x-4 gap-y-4 sm:grid sm:grid-cols-2 lg:gap-y-7";

function FloatingInput({
  id,
  label,
  value,
  onChange,
  disabled,
  type = "text",
  error,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  type?: React.HTMLInputTypeAttribute;
  error?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <Input
        id={id}
        type={type}
        disabled={disabled}
        value={value}
        onChange={onChange}
        onWheel={
          type === "number"
            ? (e) => (e.target as HTMLInputElement).blur()
            : undefined
        }
        className={floatingInputClass}
      />
      <label htmlFor={id} className={floatingLabelClass}>
        {label}
      </label>
      {error ? (
        <p className="ml-2 mt-1 text-xs text-brand-textRed">{error}</p>
      ) : null}
    </div>
  );
}

function FloatingTextarea({
  id,
  label,
  value,
  onChange,
  rows,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows: number;
  className?: string;
}) {
  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={onChange}
        className={floatingTextareaClass}
      />
      <label htmlFor={id} className={floatingTextareaLabelClass}>
        {label}
      </label>
    </div>
  );
}

/**
 * Seller asset-transfer event form — same input chrome as banker EventFormV2
 * (floating labels, peer focus ring), fields from asset-transfer-assets.
 */
export default function SellerAssetTransferEventForm() {
  const form = useBetaForm({ ...initialAssetTransferFormValues });

  useEffect(() => {
    form.setValidationRules({
      borrower_name: { presence: { allowEmpty: false, message: "^Required" } },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only validation rules
  }, []);

  const handleSubmit = async () => {
    if (!form.validate()) return;
    form.startProcessing();
    try {
      await new Promise((r) => setTimeout(r, 400));
      console.log("Seller asset transfer form", form.value);
    } finally {
      form.finishProcessing();
    }
  };

  const field = (key: AssetTransferFormKey) =>
    form.getField(key as string) ?? "";

  const setField =
    (key: AssetTransferFormKey) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      form.setField(key as string, e.target.value);
    };

  const setFieldInput =
    (key: AssetTransferFormKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setField(key as string, e.target.value);
    };

  return (
    <div className="box-border mt-[30px] w-full min-w-0 max-w-full pb-16">
      <p className="font-openSans font-normal text-web-subheading leading-6 text-brand-bgBlack">
        Create event (asset transfer)
      </p>
      <p className="mt-1 font-openSans text-sm text-brand-textGrey">
        Enter the asset transfer details below.
      </p>

      <div className={sectionClass}>
        <p className={sectionTitleClass}>Identification</p>
        <div className={gridClass}>
          <FloatingInput
            id="loan_account_number"
            label="Loan account number"
            value={field("loan_account_number")}
            onChange={setFieldInput("loan_account_number")}
          />
          <FloatingInput
            id="created_by"
            label="Created by"
            value={field("created_by")}
            onChange={setFieldInput("created_by")}
            disabled
          />
        </div>
      </div>

      <div className={sectionClass}>
        <p className={sectionTitleClass}>Borrower</p>
        <div className={gridClass}>
          <FloatingInput
            id="borrower_name"
            label="Borrower name"
            value={field("borrower_name")}
            onChange={setFieldInput("borrower_name")}
            error={form.errors.get("borrower_name")?.[0]}
          />
          <FloatingInput
            id="borrower_entity_type"
            label="Borrower entity type"
            value={field("borrower_entity_type")}
            onChange={setFieldInput("borrower_entity_type")}
          />
        </div>
      </div>

      <div className={sectionClass}>
        <p className={sectionTitleClass}>Amounts</p>
        <div className={gridClass}>
          {(
            [
              ["sanction_amount", "Sanction amount"],
              ["outstanding_principal", "Outstanding principal"],
              ["total_outstanding", "Total outstanding"],
              ["aggregate_outstanding", "Aggregate outstanding"],
            ] as const
          ).map(([id, label]) => (
            <FloatingInput
              key={id}
              id={id}
              label={label}
              value={field(id)}
              onChange={setFieldInput(id)}
            />
          ))}
          <FloatingInput
            id="dpd_days"
            label="DPD days"
            type="number"
            value={field("dpd_days")}
            onChange={setFieldInput("dpd_days")}
          />
          <FloatingInput
            id="number_of_accounts"
            label="Number of accounts"
            type="number"
            value={field("number_of_accounts")}
            onChange={setFieldInput("number_of_accounts")}
          />
        </div>
      </div>

      <div className={sectionClass}>
        <p className={sectionTitleClass}>Asset details</p>
        <div className={gridClass}>
          <FloatingTextarea
            id="secured_asset_description"
            label="Secured asset description"
            rows={3}
            value={field("secured_asset_description")}
            onChange={setField("secured_asset_description")}
            className="sm:col-span-2"
          />
          <FloatingTextarea
            id="secured_asset_location"
            label="Secured asset location"
            rows={2}
            value={field("secured_asset_location")}
            onChange={setField("secured_asset_location")}
            className="sm:col-span-2"
          />
          <FloatingInput
            id="security_type"
            label="Security type"
            value={field("security_type")}
            onChange={setFieldInput("security_type")}
          />
          <FloatingInput
            id="valuation_amount"
            label="Valuation amount"
            value={field("valuation_amount")}
            onChange={setFieldInput("valuation_amount")}
          />
          <FloatingInput
            id="valuation_date"
            label="Valuation date"
            type="date"
            value={field("valuation_date")}
            onChange={setFieldInput("valuation_date")}
          />
          <FloatingInput
            id="npa_date"
            label="NPA date"
            type="date"
            value={field("npa_date")}
            onChange={setFieldInput("npa_date")}
          />
        </div>
      </div>

      <div className={sectionClass}>
        <p className={sectionTitleClass}>Legal &amp; portfolio</p>
        <div className={gridClass}>
          <FloatingInput
            id="legal_status"
            label="Legal status"
            value={field("legal_status")}
            onChange={setFieldInput("legal_status")}
          />
          <FloatingInput
            id="litigation_status"
            label="Litigation status"
            value={field("litigation_status")}
            onChange={setFieldInput("litigation_status")}
          />
          <FloatingInput
            id="geographical_spread"
            label="Geographical spread"
            value={field("geographical_spread")}
            onChange={setFieldInput("geographical_spread")}
          />
          <FloatingInput
            id="portfolio_segment"
            label="Portfolio segment"
            value={field("portfolio_segment")}
            onChange={setFieldInput("portfolio_segment")}
          />
          <FloatingInput
            id="data_cutoff_date"
            label="Data cutoff date"
            type="date"
            value={field("data_cutoff_date")}
            onChange={setFieldInput("data_cutoff_date")}
          />
          <FloatingInput
            id="status"
            label="Status"
            value={field("status")}
            onChange={setFieldInput("status")}
          />
        </div>
      </div>

      <div className="mt-6 flex w-full min-w-0 max-w-full justify-center pt-2">
        <Button
          type="button"
          className="mt-4 w-full max-w-[240px] text-white py-3 px-5"
          onClick={handleSubmit}
          disabled={form.busy}
        >
          {form.busy ? <LoadingSpinner /> : "Save & submit"}
        </Button>
      </div>
    </div>
  );
}
